<script lang="ts" context="module">
  let videoElement: HTMLVideoElement;
  export function setVideoTime(time: number) {
    videoElement.currentTime = time;
  }
  export function playVideo() {
    if (!videoElement.paused) return false;
    videoElement.play();
  }
</script>

<script lang="ts">
  import {thumbnail, subtitles} from '@/store';
  import VideoControls from './VideoControls.svelte';
  import type {Video} from '@/types/project';
  import {afterUpdate, createEventDispatcher, onDestroy, onMount} from 'svelte';
  import {toTime} from '@/utils/common';

  export let play: boolean = false;
  export let currentTime: number = 0;

  let currentProgress = 0;
  let progressBar: any;
  let selectedVideo: Video | null = null;
  let vidUrl: string = '';

  const dispatch = createEventDispatcher();
  $: if (play) {
    playVideo();
    play = false;
  }
  const subscription = thumbnail.subscribe(p => {
    selectedVideo = p.video;
    vidUrl = selectedVideo?.url!;
  });
  onMount(() => {});

  onDestroy(subscription);

  // afterUpdate(() => {
  //   videoElement.currentTime = currentTime;
  //   updateProgressBar();
  // });

  let videoLoaded = false;
  let videoPlayPromise: Promise<void> | null = null;

  const playVideo = () => {
    if (!videoLoaded) return console.log('video not yet loaded');

    if (!videoElement.paused) return videoElement.pause();
    return (videoPlayPromise = videoElement.play());
  };

  const pauseVideo = () => {
    if (!videoLoaded) return console.log('video not yet loaded');

    if (videoElement.paused) return;
    if (videoPlayPromise) return videoPlayPromise.then(() => videoElement.pause());
    videoElement.pause();
  };

  const stopVideo = () => {
    videoElement.load();
    currentTime = 0;
    updateProgressBar();
  };

  const seekTimeline = (e: any) => {
    const {value} = e.detail;
    const derived = value / 100;

    videoElement.currentTime = videoElement.duration * derived;
    dispatch('video-seek', {time: videoElement.currentTime});
  };

  const adjustSeek = (e: any) => {
    const value = e.detail;
    console.log(videoElement.currentTime);
    videoElement.currentTime = videoElement.currentTime + value;
    dispatch('adjust-seek', {time: videoElement.currentTime});
  };

  $: (() => {
    if (currentTime && videoElement) {
      videoElement.currentTime = currentTime;
      updateProgressBar();
    }
  })();

  const updateProgressBar = () => {
    let bar = progressBar.getProgressElement();
    bar.value = videoElement.currentTime === 0 ? 0 : (videoElement.currentTime / videoElement.duration) * 100;
    progressBar.changeRunnerColor(bar);
    return bar.value;
  };

  const progressLoop = () => {
    currentProgress = updateProgressBar();
  };

  const resize = () => {
    const snapshot = document.getElementById('snapshot') as HTMLImageElement;
    if (snapshot) {
      var naturalH = snapshot.naturalHeight;
      var naturalW = snapshot.naturalWidth;
      var ratio = naturalH / naturalW;
      var newW = snapshot.clientHeight / ratio;
      snapshot.style.width = newW + 'px';
    }
  };

  const setVideoLoaded = (event: Event) => (videoLoaded = true);

  onMount(() => {
    videoElement.addEventListener('loadedmetadata', setVideoLoaded);
  });

  onDestroy(() => {
    videoElement.removeEventListener('loadedmetadata', setVideoLoaded);
  });
  export let insertSnapshot: () => void;
  export let removeBg: () => void;
  export let insertNoBg: () => void;
  export let hideSwitch: string = 'hidden';

  function onError(str: string) {
    if (str) alert(str);
    if (selectedVideo && selectedVideo.url) {
      vidUrl = selectedVideo?.url!;
      videoElement.load();
    }
  }
</script>

<div class="flex h-full w-full flex-grow justify-center overflow-hidden">
  <div class="flex h-full w-full flex-col items-center justify-center">
    <div class="flex w-full flex-row items-start justify-between py-4" style="height: 55vh;">
      <!-- Video Section -->
      <div class="flex h-full w-full flex-col items-center justify-start px-2">
        <p class="py-3 text-lg font-bold {hideSwitch}">Original</p>
        <div class="h-0 w-full flex-grow">
          <div class="aspect-video h-full w-full">
            <video
              crossorigin="anonymous"
              id="videoseek"
              class="h-full w-full rounded-lg"
              controls={false}
              preload="metadata"
              on:error={() =>
                onError("Video is not available yet. Retrying. Refresh the browser if it's still not loading")}
              on:invalid={() => onError("Loading failed. Retrying. Refresh the browser if it's still not loading")}
              on:abort={() => onError('')}
              on:stalled={() => onError('')}
              bind:this={videoElement}
              on:ended={stopVideo}
              on:timeupdate={() => progressLoop()}
            >
              <source src={vidUrl} type="video/mp4" />
              <track kind="captions" />
            </video>
          </div>
        </div>
        <div class="mt-4 flex w-full space-x-2 px-4">
          <button
            on:click={insertSnapshot}
            class="flex w-full items-center justify-center rounded-xl border border-primary bg-white px-2 py-3 text-primary hover:bg-primary hover:text-white"
          >
            Insert Snapshot
          </button>
          <button
            on:click={removeBg}
            class="flex w-full items-center justify-center rounded-xl border border-primary bg-primary px-2 py-3 text-white"
          >
            Remove Background
          </button>
        </div>
      </div>

      <!-- Snapshot Section -->
      <div class="flex h-full w-full flex-col items-center justify-start px-2 {hideSwitch}">
        <p class="py-3 text-lg font-bold">Background removed</p>
        <div class="h-0 flex-grow border border-primary">
          <img
            on:load={resize}
            src="https://storage.googleapis.com/sermonshots-dev-bucket/uploads%2F9ad7f200-11e9-4d4c-9fb1-8d9d68c8b028.jpg"
            alt="Snapshot"
            id="snapshot"
            class="h-full rounded-lg object-cover"
          />
        </div>
        <div class="mt-4 flex w-full items-center justify-center px-4">
          <button
            on:click={insertNoBg}
            class="flex w-1/2 items-center justify-center rounded-xl border border-primary bg-white px-2 py-3 text-primary hover:bg-primary hover:text-white"
          >
            Insert Snapshot
          </button>
        </div>
      </div>
    </div>

    <!-- Video Controls -->
    <div id="vidcontrol" class="absolute bottom-0 w-full">
      <VideoControls
        bind:progressBar
        showDuration={true}
        on:stop-video={stopVideo}
        on:play-video={playVideo}
        on:pause-video={pauseVideo}
        on:seek-timeline={seekTimeline}
        on:adjust-seek={adjustSeek}
        progress={currentProgress}
        duration={selectedVideo?.metadata?.duration}
      />
    </div>
  </div>
</div>

<style lang="postcss">
</style>
